import React from 'react';
import { cn } from '@/lib/utils';

export interface ConsultationScrollAreaProps {
  className?: string;
  children: React.ReactNode;
}

// Card corners using this component are rounded-3xl/rounded-[24px]. The track
// sits just inside that radius, so its vertical inset must clear the corner's
// curve (>=24px) — otherwise the thumb's top/bottom tip pokes past the rounded
// edge into the page background behind it.
const TRACK_INSET_PX = 26;
const MIN_THUMB_PX = 30;

/**
 * Scroll container with a slim custom scrollbar (matching the Figma
 * consultation cards) instead of a native one. The thumb's length is the
 * viewport/content ratio (never shorter than MIN_THUMB_PX) and its position
 * follows the scroll offset.
 *
 * The scrolling element is a plain overflow-y-auto div with its native
 * scrollbar hidden. (Radix's ScrollArea viewport only scrolls vertically while
 * one of its own Scrollbar components is mounted, so it can't be used with a
 * custom thumb.)
 */
export function ConsultationScrollArea({ className, children }: ConsultationScrollAreaProps) {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [scrollInfo, setScrollInfo] = React.useState({
    scrollTop: 0,
    scrollHeight: 0,
    clientHeight: 0,
  });

  const updateScroll = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    setScrollInfo((prev) =>
      prev.scrollTop === el.scrollTop &&
      prev.scrollHeight === el.scrollHeight &&
      prev.clientHeight === el.clientHeight
        ? prev
        : {
            scrollTop: el.scrollTop,
            scrollHeight: el.scrollHeight,
            clientHeight: el.clientHeight,
          }
    );
  }, []);

  // Scroll events cover scrolling; the observer covers the card resizing and
  // its content growing/shrinking (the live consultation keeps filling in).
  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScroll();
    const observer = new ResizeObserver(updateScroll);
    observer.observe(el);
    if (contentRef.current) observer.observe(contentRef.current);
    return () => observer.disconnect();
  }, [updateScroll]);

  const { scrollTop, scrollHeight, clientHeight } = scrollInfo;
  const maxScroll = Math.max(0, scrollHeight - clientHeight);
  const isScrollable = maxScroll > 0;

  // Thumb math runs over the track (the viewport minus the corner insets), so
  // the thumb reaches both ends of it without leaving the card's rounded edge.
  const trackHeight = Math.max(0, clientHeight - TRACK_INSET_PX * 2);
  const thumbHeight = isScrollable
    ? Math.min(trackHeight, Math.max(MIN_THUMB_PX, (clientHeight / scrollHeight) * trackHeight))
    : 0;
  const thumbTop = isScrollable ? (scrollTop / maxScroll) * (trackHeight - thumbHeight) : 0;

  return (
    <div className={cn('relative overflow-hidden', className)}>
      <div
        ref={scrollRef}
        onScroll={updateScroll}
        className="h-full w-full overflow-y-auto rounded-[inherit] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <div ref={contentRef}>{children}</div>
      </div>
      <div
        aria-hidden="true"
        className="pointer-events-none absolute right-0 w-[4px]"
        style={{ top: TRACK_INSET_PX, bottom: TRACK_INSET_PX }}
      >
        {isScrollable && (
          <div
            className="absolute left-0 w-[4px] rounded-full bg-[#BCBCBE]"
            style={{ height: thumbHeight, transform: `translateY(${thumbTop}px)` }}
          />
        )}
      </div>
    </div>
  );
}
